'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './Dialog';
import { FlexContainer } from './FlexContainer';
import { Separator } from './Separator';
import { Button } from './Button';
import { isEventFromOverlayLayer } from './dismissEvents';
import type { CloseRequestRenderProps, ConfirmBeforeClose } from './SheetWrapper';
export type ModalWrapperProps = {
  isOpen: boolean;
  onClose: () => void;
  children?: React.ReactNode | ((props: CloseRequestRenderProps) => React.ReactNode);
  closeable?: boolean;
  preventOutsideClose?: boolean;
  description?: string;
  footer?: React.ReactNode | ((props: CloseRequestRenderProps) => React.ReactNode);
  header?: React.ReactNode;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  isFirstModal?: boolean;
  /** If enabled, shows a confirmation modal before closing */
  confirmBeforeClose?: ConfirmBeforeClose;
  /** Title for the confirmation modal. Defaults to 'Unsaved changes' */
  confirmationTitle?: string;
  /** Description for the confirmation modal. Defaults to 'You have unsaved changes. Are you sure you want to close?' */
  confirmationDescription?: string;
  /** Text for the confirm button. Defaults to 'Close anyway' */
  confirmButtonText?: string;
  /** Text for the cancel button. Defaults to 'Keep editing' */
  cancelButtonText?: string;
};
export function resolveModalWrapperContent(content: ModalWrapperProps['children'], closeRequestProps: CloseRequestRenderProps): React.ReactNode {
  return typeof content === 'function' ? content(closeRequestProps) : content;
}
export default function ModalWrapper(t0) {
  const $ = _c(48);
  const {
    children,
    closeable: t1,
    preventOutsideClose: t2,
    footer,
    header,
    size: t3,
    isFirstModal: t4,
    confirmBeforeClose: t5,
    confirmationTitle: t6,
    confirmationDescription: t7,
    confirmButtonText: t8,
    cancelButtonText: t9,
    ...props
  } = t0;
  const closeable = t1 === undefined ? true : t1;
  const preventOutsideClose = t2 === undefined ? false : t2;
  const size = t3 === undefined ? "md" : t3;
  const isFirstModal = t4 === undefined ? true : t4;
  const confirmBeforeClose = t5 === undefined ? false : t5;
  const confirmationTitle = t6 === undefined ? "Unsaved changes" : t6;
  const confirmationDescription = t7 === undefined ? "You have unsaved changes. Are you sure you want to close?" : t7;
  const confirmButtonText = t8 === undefined ? "Close anyway" : t8;
  const cancelButtonText = t9 === undefined ? "Keep editing" : t9;
  const [showConfirmation, setShowConfirmation] = useState(false);
  const confirmationConfig = typeof confirmBeforeClose === "object" ? confirmBeforeClose : undefined;
  const resolvedConfirmationTitle = confirmationConfig?.title ?? confirmationTitle;
  const resolvedConfirmationDescription = confirmationConfig?.description ?? confirmationDescription;
  const resolvedConfirmButtonText = confirmationConfig?.confirmText ?? confirmButtonText;
  const resolvedCancelButtonText = confirmationConfig?.cancelText ?? cancelButtonText;
  useEffect(() => {
    if (!props.isOpen) {
      setShowConfirmation(false);
    }
  }, [props.isOpen]);
  const handleCloseAttempt = function handleCloseAttempt(event) {
    const shouldConfirm = typeof confirmBeforeClose === "object" ? confirmBeforeClose.when() : typeof confirmBeforeClose === "function" ? confirmBeforeClose() : confirmBeforeClose;
    event?.preventDefault();
    if (shouldConfirm && !showConfirmation) {
      setShowConfirmation(true);
    } else {
      if (!shouldConfirm) {
        props.onClose();
      }
    }
  };
  const handleOpenChange = function handleOpenChange(open) {
    if (!open && closeable) {
      handleCloseAttempt();
    }
  };
  const handleConfirmClose = function handleConfirmClose() {
    setShowConfirmation(false);
    props.onClose();
  };
  let t10;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = function handleCancelClose() {
      setShowConfirmation(false);
    };
    $[0] = t10;
  } else {
    t10 = $[0];
  }
  const handleCancelClose = t10;
  const T0 = Dialog;
  const t11 = props.isOpen;
  const T1 = DialogContent;
  const t12 = props.description;
  const t13 = e => confirmBeforeClose && handleCloseAttempt(e);
  const t14 = e_0 => {
    if (!closeable) {
      e_0.preventDefault();
    } else {
      if (confirmBeforeClose) {
        handleCloseAttempt(e_0);
      }
    }
  };
  const t15 = e_1 => {
    if (!closeable || preventOutsideClose || isEventFromOverlayLayer(e_1)) {
      e_1.preventDefault();
    } else {
      if (confirmBeforeClose) {
        handleCloseAttempt(e_1);
      }
    }
  };
  const t16 = _temp;
  const t17 = header !== undefined ? header : (props.title || props.description) && <DialogHeader>{props.title && <DialogTitle>{props.title}</DialogTitle>}{props.description && <DialogDescription>{props.description}</DialogDescription>}</DialogHeader>;
  const t18 = resolveModalWrapperContent(children, {
    requestClose: () => handleCloseAttempt()
  });
  const t19 = footer && <FlexContainer className="gap-6 mt-2 w-full" direction="column" items="end"><Separator /><DialogFooter>{resolveModalWrapperContent(footer, {
        requestClose: () => handleCloseAttempt()
      })}</DialogFooter></FlexContainer>;
  let t20;
  if ($[1] !== T1 || $[2] !== closeable || $[3] !== isFirstModal || $[4] !== props || $[5] !== size || $[6] !== t13 || $[7] !== t14 || $[8] !== t15 || $[9] !== t16 || $[10] !== t17 || $[11] !== t18 || $[12] !== t19) {
    t20 = <T1 aria-describedby={t12} closeable={closeable} onCloseClick={t13} onEscapeKeyDown={t14} onInteractOutside={t15} onOpenAutoFocus={t16} showOverlayBackground={isFirstModal} size={size}>{t17}{t18}{t19}</T1>;
    $[1] = T1;
    $[2] = closeable;
    $[3] = isFirstModal;
    $[4] = props;
    $[5] = size;
    $[6] = t13;
    $[7] = t14;
    $[8] = t15;
    $[9] = t16;
    $[10] = t17;
    $[11] = t18;
    $[12] = t19;
    $[13] = t20;
  } else {
    t20 = $[13];
  }
  let t21;
  if ($[14] !== T0 || $[15] !== handleOpenChange || $[16] !== props || $[17] !== t20) {
    t21 = <T0 onOpenChange={handleOpenChange} open={t11}>{t20}</T0>;
    $[14] = T0;
    $[15] = handleOpenChange;
    $[16] = props;
    $[17] = t20;
    $[18] = t21;
  } else {
    t21 = $[18];
  }
  let t22;
  if ($[19] !== handleCancelClose) {
    t22 = open_0 => !open_0 && handleCancelClose();
    $[19] = handleCancelClose;
    $[20] = t22;
  } else {
    t22 = $[20];
  }
  let t23;
  if ($[21] !== resolvedConfirmationTitle) {
    t23 = <DialogTitle>{resolvedConfirmationTitle}</DialogTitle>;
    $[21] = resolvedConfirmationTitle;
    $[22] = t23;
  } else {
    t23 = $[22];
  }
  let t24;
  if ($[23] !== resolvedConfirmationDescription) {
    t24 = <DialogDescription>{resolvedConfirmationDescription}</DialogDescription>;
    $[23] = resolvedConfirmationDescription;
    $[24] = t24;
  } else {
    t24 = $[24];
  }
  let t25;
  if ($[25] !== t23 || $[26] !== t24) {
    t25 = <DialogHeader>{t23}{t24}</DialogHeader>;
    $[25] = t23;
    $[26] = t24;
    $[27] = t25;
  } else {
    t25 = $[27];
  }
  let t26;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Separator />;
    $[28] = t26;
  } else {
    t26 = $[28];
  }
  let t27;
  if ($[29] !== handleCancelClose || $[30] !== resolvedCancelButtonText) {
    t27 = <Button onClick={handleCancelClose} variant="quaternary">{resolvedCancelButtonText}</Button>;
    $[29] = handleCancelClose;
    $[30] = resolvedCancelButtonText;
    $[31] = t27;
  } else {
    t27 = $[31];
  }
  let t28;
  if ($[32] !== handleConfirmClose || $[33] !== resolvedConfirmButtonText) {
    t28 = <Button onClick={handleConfirmClose} variant="destructive">{resolvedConfirmButtonText}</Button>;
    $[32] = handleConfirmClose;
    $[33] = resolvedConfirmButtonText;
    $[34] = t28;
  } else {
    t28 = $[34];
  }
  let t29;
  if ($[35] !== t27 || $[36] !== t28) {
    t29 = <FlexContainer className="gap-6 mt-2 w-full" direction="column" items="end">{t26}<DialogFooter><FlexContainer className="gap-4">{t27}{t28}</FlexContainer></DialogFooter></FlexContainer>;
    $[35] = t27;
    $[36] = t28;
    $[37] = t29;
  } else {
    t29 = $[37];
  }
  let t30;
  if ($[38] !== t25 || $[39] !== t29) {
    t30 = <DialogContent onOpenAutoFocus={_temp2} showOverlayBackground={true} size="sm">{t25}{t29}</DialogContent>;
    $[38] = t25;
    $[39] = t29;
    $[40] = t30;
  } else {
    t30 = $[40];
  }
  let t31;
  if ($[41] !== showConfirmation || $[42] !== t22 || $[43] !== t30) {
    t31 = <Dialog onOpenChange={t22} open={showConfirmation}>{t30}</Dialog>;
    $[41] = showConfirmation;
    $[42] = t22;
    $[43] = t30;
    $[44] = t31;
  } else {
    t31 = $[44];
  }
  let t32;
  if ($[45] !== t21 || $[46] !== t31) {
    t32 = <>{t21}{t31}</>;
    $[45] = t21;
    $[46] = t31;
    $[47] = t32;
  } else {
    t32 = $[47];
  }
  return t32;
}
function _temp2(e_3) {
  e_3.preventDefault();
  e_3.stopPropagation();
}
function _temp(e_2) {
  e_2.preventDefault();
  e_2.stopPropagation();
}